import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import GrCard from "./GrCard";
import { gr } from "../tokens";

// The shape of a feed post while posts load (as social feeds do): author line, a few text lines, an
// optional photo block and the action bar, softly shimmering. It holds about a post's height, so the
// real posts replace it without the page jumping. `count` cards are shown; screen readers hear one
// "Loading posts".
const shimmerSx = { bgcolor: gr.color.line2 };

function OnePost({ media }) {
  return (
    <GrCard as="div" padding="md">
      <Box sx={{ display: "flex", alignItems: "center", gap: gr.space[3] }}>
        <Skeleton
          variant="circular"
          width={gr.control.md}
          height={gr.control.md}
          sx={shimmerSx}
        />
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width="35%" sx={shimmerSx} />
          <Skeleton variant="text" width="22%" sx={shimmerSx} />
        </Box>
      </Box>
      <Box sx={{ mt: gr.space[3] }}>
        <Skeleton variant="text" width="92%" sx={shimmerSx} />
        <Skeleton variant="text" width="70%" sx={shimmerSx} />
      </Box>
      {media ? (
        <Skeleton
          variant="rectangular"
          sx={{
            ...shimmerSx,
            mt: gr.space[3],
            height: "auto",
            aspectRatio: "16 / 9",
            borderRadius: gr.radius.tile,
          }}
        />
      ) : null}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: gr.space[6],
          mt: gr.space[4],
          pt: gr.space[3],
          borderTop: `1px solid ${gr.color.line2}`,
        }}
      >
        <Skeleton
          variant="text"
          sx={{ ...shimmerSx, justifySelf: "center", width: "40%" }}
        />
        <Skeleton
          variant="text"
          sx={{ ...shimmerSx, justifySelf: "center", width: "40%" }}
        />
      </Box>
    </GrCard>
  );
}

OnePost.propTypes = { media: PropTypes.bool };

export default function GrPostSkeleton({ count = 1, label = "Loading posts" }) {
  return (
    <Box
      role="status"
      aria-label={label}
      sx={{ display: "flex", flexDirection: "column", gap: gr.space[4] }}
    >
      {Array.from({ length: count }, (_, index) => (
        // The first card has a photo block, so the loading feed looks like a real one.
        <OnePost key={index} media={index === 0} />
      ))}
    </Box>
  );
}

GrPostSkeleton.propTypes = {
  count: PropTypes.number,
  label: PropTypes.string,
};
